// packages/core/src/learning/school-modules/rich-life-ramit.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Rich life (Ramit)". surface: 'school',
// homeSchool: 'rich-life-ramit'. module id 'school-rich-life-ramit' matches
// the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #1abc9c) — flag for the presentation-tuning pass.
// tier/pillar: conscious-spending / money-system lens → tier 'control',
// pillar 'cash-flow-control'. (Big-wins lesson leans income/psychology, but
// the pack's center of gravity is conscious spending + automation, so
// cash-flow-control is the cleanest single mapping.)
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_RICH_LIFE_RAMIT_MODULE: LearningModule = {
	id: 'school-rich-life-ramit',
	title: 'Rich life (Ramit)',
	description:
		'The lens that funds what you actually value — conscious spending, automation, and big wins over guilt budgeting, kept honest against your real numbers.',
	emoji: '💸', // PLACEHOLDER — tune in presentation pass
	accentColor: '#1abc9c', // from SchoolMeta color — tune in presentation pass
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 0,
	surface: 'school',
	homeSchool: 'rich-life-ramit',
	lessons: [
		{
			id: 'school-rich-life-ramit-01',
			title: 'Your rich life is personal',
			description:
				'This lesson teaches the central Rich life idea: money should support a life you actually value, not someone else’s status script.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['rich-life-ramit'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['personal-rich-life', 'values-spending', 'status-script'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'personal rich life',
				'values based spending',
				'status script',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Rich life starts with a question most budgets skip: what do you actually want your money to do for you?',
				},
				{
					type: 'paragraph',
					text: 'This lens believes a good money life is personal. One person may care about travel, another about family, another about fitness, food, housing, art, freedom, or never feeling rushed.',
				},
				{
					type: 'bullet_list',
					items: [
						'Name what matters',
						'Cut what does not',
						'Stop copying someone else’s version',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating money like a scoreboard instead of a design tool.',
				},
				{
					type: 'example',
					text: 'A person may spend heavily on concerts and live simply everywhere else. Another may care deeply about a calm home and barely care about eating out. The point is not matching choices. The point is intention.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that personal values can become an excuse if the numbers do not work. A rich life still needs income, stability, savings, debt awareness, and real tradeoffs.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard and freedom plan. You are asking whether your money supports the life you say matters, not whether it impresses strangers.',
				},
				{
					type: 'key_takeaway',
					text: 'Rich life works when your money reflects your values, not your guilt or someone else’s script.',
				},
			],
			audioScript:
				'Rich life starts with a simple question that most money advice avoids: what do you actually want your money to do for you?\n\nNot what should sound responsible. Not what looks impressive online. Not what your parents, friends, coworkers, or culture think a successful life should look like. You. Your real life. Your real values. Your real energy. Your real priorities.\n\nThat is why this lens is different from a normal budget conversation. A normal budget often starts with guilt. Spend less here. Stop buying that. Cut this. Be more disciplined. Rich life starts by asking what is worth spending on. That does not mean every want deserves money. It means your plan should know the difference between spending that gives you life and spending that just leaks.\n\nFor one person, a rich life might mean traveling twice a year and keeping everything else simple. For another, it might mean living near family. For someone else, it might mean fitness, good food, a quiet apartment, the ability to help parents, a motorcycle, photography, school, tattoos, or time to write. The point is not that one version is better. The point is that money should serve the life you would actually choose.\n\nThis protects you from copying other people’s status scripts. Some people spend to look rich. Some save to look disciplined. Some invest to look smart. Some cut spending because they think suffering proves maturity. Rich life pushes against that. It asks whether the choice belongs to you.\n\nBut this lens has a real blind spot. Values can become an excuse. Someone can say travel is part of their rich life while ignoring debt stress, no emergency runway, unstable income, or a future that keeps getting weaker. Someone can call every upgrade intentional when it is really lifestyle creep. That is not the strong version.\n\nThe strong version combines desire with structure. You name what matters. You cut what does not. You automate the basics. You grow income when possible. You test the tradeoffs. You do not pretend every want is wise, but you also do not build a life around guilt.\n\nInside Nisk, this starts with your Dashboard. What does your current money picture say about your actual priorities? Are you funding what matters, or are random defaults eating the room? Then a freedom plan helps you connect the values to real numbers. Not as personal advice. As a mirror.\n\nYour rich life is personal. That means no one else can define it for you. It also means you have to be honest enough to fund it on purpose.',
			flashcards: [
				{
					id: 'school-rich-life-ramit-01-fc-1',
					role: 'main_idea',
					front: 'What is a rich life?',
					back: 'A money life built around your real values and priorities.',
				},
				{
					id: 'school-rich-life-ramit-01-fc-2',
					role: 'watch_out',
					front: 'What is the status script?',
					back: 'Copying what looks impressive instead of choosing what fits.',
				},
				{
					id: 'school-rich-life-ramit-01-fc-3',
					role: 'use_it',
					front: 'How do you use this lens?',
					back: 'Name what matters, then check whether your money supports it.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your current money picture',
			},
			revision: 1,
		},
		{
			id: 'school-rich-life-ramit-02',
			title: 'Spend boldly on what matters',
			description:
				'This lesson teaches conscious spending: cut clearly from low value areas so money can support what matters most.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['rich-life-ramit'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['conscious-spending', 'money-dials', 'value-tradeoff'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['conscious spending', 'money dials', 'value tradeoff'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Rich life does not treat all spending as bad. It treats unclear spending as the problem.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes you can spend boldly on what matters if you cut clearly from what does not. The goal is not guilt. The goal is alignment.',
				},
				{
					type: 'bullet_list',
					items: [
						'Protect high value spending',
						'Cut low value spending',
						'Make the tradeoff visible',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is cutting everything a little and still feeling deprived everywhere.',
				},
				{
					type: 'example',
					text: 'Someone who loves travel may choose a cheaper apartment, fewer subscriptions, and simple weekday meals so a big trip feels funded instead of guilty.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that bold spending can become a fancy label for impulse spending. If the choice is not planned, affordable, and connected to values, it may just be lifestyle creep with better branding.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a reflection and planning habit. You are asking which spending deserves protection and which spending is stealing from the life you actually want.',
				},
				{
					type: 'key_takeaway',
					text: 'Conscious spending means saying yes harder because you said no clearly.',
				},
			],
			audioScript:
				'Rich life is not anti spending. That is the whole point. Some money advice makes it sound like spending is the enemy and the best person is the one who spends the least. Rich life disagrees. It says spending can be powerful when it is pointed at what actually matters.\n\nThe problem is not spending. The problem is unclear spending.\n\nUnclear spending is the money that disappears without a real yes behind it. Subscriptions you forgot about. Food delivery you barely enjoy. Clothes that do not fit your life. Upgrades you bought because you were bored. Social plans you did not want. Convenience that became automatic. Random purchases that gave you a tiny hit and then became clutter.\n\nConscious spending asks you to make the tradeoff visible. What spending makes your life meaningfully better? What spending feels good for ten minutes and then adds pressure? What are your money dials, the areas where spending more actually feels worth it?\n\nFor one person, that dial might be travel. For another, it might be fitness, home, food, family, education, beauty, music, hobbies, or time saving help. The key is that your dials are yours. You do not need to justify them to someone with different values. But you do need to fund them honestly.\n\nThat means cutting clearly. If travel matters, maybe random shopping matters less. If a calm home matters, maybe eating out matters less. If fitness matters, maybe a car upgrade matters less. If helping family matters, maybe status spending matters less. Rich life is not about having everything. It is about choosing what gets the strongest yes.\n\nThe mistake is cutting everything a little. You reduce the things you love, keep the things you do not care about, and end up feeling deprived without becoming freer. That is a bad trade. A better plan protects the spending that gives your life energy and cuts the spending that only fills space.\n\nBut this lens can be misused. People can call anything part of their rich life. A luxury car, expensive trips, constant shopping, premium everything. Maybe some of it is worth it. Maybe it is not. The test is not whether it sounds fun. The test is whether it fits your numbers, your responsibilities, and your long term path.\n\nInside Nisk, this can become a simple audit. Name the spending you want to protect. Name the spending you are willing to cut. Then test whether the plan actually creates room. Conscious spending is powerful because it replaces vague guilt with clear tradeoffs.\n\nSpend boldly where it matters. Cut clearly where it does not. That is the Rich life lens.',
			flashcards: [
				{
					id: 'school-rich-life-ramit-02-fc-1',
					role: 'main_idea',
					front: 'What is conscious spending?',
					back: 'Spending more on what matters by cutting what matters less.',
				},
				{
					id: 'school-rich-life-ramit-02-fc-2',
					role: 'watch_out',
					front: 'What is unclear spending?',
					back: 'Money that disappears without a real value based yes.',
				},
				{
					id: 'school-rich-life-ramit-02-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Pick what deserves protection and what should be cut.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name your money dials',
				prompt: 'Write three spending areas that genuinely matter to you and three that mostly drain money without much value.',
			},
			revision: 1,
		},
		{
			id: 'school-rich-life-ramit-03',
			title: 'Automation protects your future self',
			description:
				'This lesson teaches why Rich life favors systems that handle good money behavior before willpower gets tested.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['rich-life-ramit'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['automation-system', 'willpower-limits', 'default-behavior'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'automation system',
				'willpower limits',
				'default behavior',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Willpower is a weak money plan. Rich life knows that good intentions often lose to stress, tiredness, temptation, and daily noise.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes automation can protect your future self. When important money moves happen by default, you do not have to win the same argument every month.',
				},
				{
					type: 'bullet_list',
					items: [
						'Automate the basics',
						'Reduce repeated decisions',
						'Make good behavior easier',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is relying on motivation for bills, saving, and investing decisions that should be system driven.',
				},
				{
					type: 'example',
					text: 'A person who manually saves whatever is left may save nothing after life happens. A person who moves money first builds the habit before spending gets loud.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that automation can make people stop paying attention. A strong system still needs review, especially when income, bills, debt, or goals change.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard. You are checking whether your system supports your priorities before emotions and distractions take over.',
				},
				{
					type: 'key_takeaway',
					text: 'Automation works because it makes your better decision the default.',
				},
			],
			audioScript:
				'Rich life is practical about human behavior. It does not pretend you will always feel motivated, focused, disciplined, and calm. You will not. You will get tired. You will get busy. You will have stressful weeks. You will want things. You will forget. You will avoid looking at your numbers when life feels messy.\n\nThat is why this lens likes automation.\n\nAutomation is not magic. It just moves important decisions out of the emotional danger zone. If bills, saving, investing, and basic money flows depend on you making the right choice at the end of every month, life has too many chances to interfere. By the time the month ends, the money may already be gone. Not because you are bad with money. Because the system made spending easier than saving.\n\nRich life says to flip the default. Make the important money moves happen early, clearly, and with less drama. Then you can spend from what is left with less guilt. This is not about removing responsibility. It is about designing a system that does not require constant heroic discipline.\n\nThink about two people. One says, I will save whatever is left. The other sets up a system where money moves toward bills, goals, and future priorities first. The first person has to fight the same fight every month. The second person still has to be responsible, but the system helps.\n\nThis matters because willpower is inconsistent. Systems are repeatable. A good system can protect you on average days, not just perfect days.\n\nBut automation has a blind spot. It can make you stop paying attention. Subscriptions can keep charging. Bills can rise. Goals can change. Income can change. Debt can get ignored. You can automate a bad setup and then let it run quietly. That is not Rich life. That is autopilot without judgment.\n\nThe mature version is automate, then review. Let the system handle the repeated basics, but keep a rhythm of checking whether the system still matches your life. Are your goals the same? Is your spending still aligned? Are your fixed costs creeping up? Is your saving still enough for what you are trying to build? Has debt changed? Has income changed?\n\nInside Nisk, the Dashboard is the mirror. It shows whether your current money picture still supports your priorities. You are not trying to stare at every transaction forever. You are trying to build a system that works, then check it before drift becomes expensive.\n\nAutomation protects your future self because it makes the better choice easier before life gets noisy.',
			flashcards: [
				{
					id: 'school-rich-life-ramit-03-fc-1',
					role: 'main_idea',
					front: 'Why automate money?',
					back: 'It makes important money moves happen before willpower gets tested.',
				},
				{
					id: 'school-rich-life-ramit-03-fc-2',
					role: 'watch_out',
					front: 'What can automation hide?',
					back: 'Rising bills, stale goals, subscriptions, or a bad system running quietly.',
				},
				{
					id: 'school-rich-life-ramit-03-fc-3',
					role: 'use_it',
					front: 'How do you use automation?',
					back: 'Automate the basics, then review the system regularly.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your money system',
			},
			revision: 1,
		},
		{
			id: 'school-rich-life-ramit-04',
			title: 'Big wins beat tiny guilt',
			description:
				'This lesson teaches why Rich life focuses on big money levers like income, housing, debt, recurring costs, and career choices.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['rich-life-ramit'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['big-wins', 'income-lever', 'decision-weight'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['big wins', 'income lever', 'decision weight'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Tiny cuts can help, but they can also become a distraction. Rich life cares about big wins because some decisions carry more weight than others.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes major levers deserve serious attention: income, career moves, housing, debt, recurring bills, investing behavior, and the expensive defaults that shape your life.',
				},
				{
					type: 'bullet_list',
					items: [
						'Grow income when possible',
						'Control large recurring costs',
						'Avoid obsessing over tiny guilt',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is feeling guilty about small purchases while ignoring the bigger structure.',
				},
				{
					type: 'example',
					text: 'Skipping a $5 drink may help a little. Negotiating a raise, lowering a major bill, avoiding a bad car payment, or changing housing costs may change the whole path more.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that big wins are not equally available to everyone. Income, location, labor market, family obligations, health, and safety all affect what can be changed.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in Simulate. You can test what a bigger income move or lower fixed cost does compared with tiny cuts.',
				},
				{
					type: 'key_takeaway',
					text: 'Rich life looks for the decisions that actually move the system.',
				},
			],
			audioScript:
				'Rich life does not ignore small spending, but it refuses to make tiny guilt the whole plan.\n\nA lot of people learn money through shame. Do not buy coffee. Do not eat out. Do not enjoy small things. Track every dollar perfectly. Feel bad when you slip. That approach can create awareness, but it can also make people obsess over tiny choices while avoiding the decisions that actually move the system.\n\nRich life cares about big wins.\n\nBig wins are the money decisions with more weight. Income. Career growth. Negotiation. Housing. Transportation. Debt interest. Recurring bills. Automation. Investing behavior. The fixed costs and repeated choices that shape your life before the month even begins.\n\nThis does not mean small spending never matters. If a small habit repeats constantly, it can become a real leak. But not all choices deserve equal emotional weight. A $5 drink is not the same as a $700 car payment. A random lunch is not the same as rent eating half your income. A small subscription is not the same as never asking for a raise, never improving skills, or never checking whether your biggest costs still make sense.\n\nThe big win lens protects you from wasting all your energy on guilt. If you spend your attention fighting over tiny purchases, you may have no energy left for the harder moves. Asking for more money. Applying for better work. Changing housing. Reviewing debt. Cutting a major recurring cost. Building a system. These can feel scarier than skipping coffee because they involve risk, rejection, planning, and identity.\n\nBut this lens also needs fairness. Big wins are not equally available to everyone. Not everyone can negotiate easily. Not everyone can move. Not everyone has a flexible career path. Not everyone can lower housing costs quickly. Health, location, family obligations, immigration status, labor markets, discrimination, and safety all affect the options. Rich life should never pretend effort fixes everything.\n\nThe practical version asks: what is the biggest lever available to you right now? Maybe it is income. Maybe it is a recurring cost. Maybe it is debt. Maybe it is housing over time. Maybe it is skills. Maybe it is simply creating a system that stops the same leaks every month.\n\nInside Nisk, this belongs in Simulate. You can compare changes. What happens if income rises by $300 a month? What happens if a fixed cost drops? What happens if you cut tiny things but ignore the big payment? The point is not to tell you what to do. The point is to see which choices carry the most weight.\n\nBig wins matter because your attention is limited. Spend it where the system can actually change.',
			flashcards: [
				{
					id: 'school-rich-life-ramit-04-fc-1',
					role: 'main_idea',
					front: 'What are big wins?',
					back: 'High weight money moves like income, housing, debt, and recurring costs.',
				},
				{
					id: 'school-rich-life-ramit-04-fc-2',
					role: 'watch_out',
					front: 'What is tiny guilt?',
					back: 'Obsessing over small purchases while ignoring bigger money levers.',
				},
				{
					id: 'school-rich-life-ramit-04-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Compare small cuts against bigger income or cost changes.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test a bigger money lever',
				scenarioType: 'side-hustle',
			},
			revision: 1,
		},
		{
			id: 'school-rich-life-ramit-05',
			title: 'Where rich life breaks down',
			description:
				'This lesson teaches the limits of the Rich life lens and how intentional spending can become lifestyle creep, avoidance, or luxury worship.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['rich-life-ramit'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['lifestyle-creep', 'luxury-worship', 'values-check'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'lifestyle creep risk',
				'luxury worship',
				'values check',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Rich life is powerful because it gives people permission to spend on what matters. It breaks down when every expensive desire gets renamed as a value.',
				},
				{
					type: 'paragraph',
					text: 'This lens can go wrong when intentional spending becomes lifestyle creep. A rich life is not a blank check for upgrades, status, and comfort without tradeoffs.',
				},
				{
					type: 'bullet_list',
					items: [
						'Values need numbers',
						'Joy needs boundaries',
						'Spending needs honesty',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using the phrase rich life to avoid hard constraints.',
				},
				{
					type: 'example',
					text: 'A person may say travel is their rich life, but if every trip creates debt stress and weakens stability, the value is real but the system is not ready.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that this lens can sound easier for people with higher income. For someone in survival mode, the first rich life step may be stability, not luxury.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, the mature move is to define the value, test the tradeoff, and keep the numbers honest. Your rich life should make life better, not more fragile.',
				},
				{
					type: 'key_takeaway',
					text: 'Rich life works when values and numbers tell the truth together.',
				},
			],
			audioScript:
				'Rich life is a strong lens because it pushes back against guilt based money advice. It says you are allowed to spend on what you love. You are allowed to want a life that feels good. You are allowed to build a money system that funds joy, comfort, beauty, travel, family, health, hobbies, and whatever genuinely matters to you.\n\nThat permission matters. But it can also be misused.\n\nThe biggest danger is turning every expensive desire into a value. A nicer apartment becomes my rich life. A luxury car becomes my rich life. Constant travel becomes my rich life. Eating out all the time becomes my rich life. Premium everything becomes my rich life. Maybe some of those things really matter. Maybe they are worth funding. But the phrase cannot replace the math.\n\nA rich life is not a blank check. It is a tradeoff.\n\nIf travel matters deeply, great. What gets cut so travel can be funded without creating debt stress? If housing matters, what does that mean for savings rate, commute, flexibility, or other goals? If restaurants matter, what spending matters less? If family support matters, what lifestyle choices need to be simpler? The lens only works when the yes and the no are both honest.\n\nThis is where Rich life can become lifestyle creep with better branding. Instead of saying, I upgraded everything because I wanted to feel successful, someone says, this is my rich life. But if the upgrades create more pressure, less runway, more debt, and less freedom, the life may be richer in appearance and poorer in options.\n\nThere is another blind spot. Rich life can sound easier for people with higher income. If someone is in survival mode, the first rich life step may not be travel, premium experiences, or big spending. It may be stable rent. A working car. A month of runway. Less debt stress. The ability to sleep. The ability to visit family without panic. That is still a rich life if it matches what the person actually needs.\n\nThe mature version of this lens is not luxury worship. It is values plus structure. You define what matters. You check what it costs. You build the system. You automate the basics. You grow income where possible. You cut what does not matter. You review the tradeoffs.\n\nInside Nisk, this is exactly the kind of idea that needs testing. Not because Nisk tells you what your rich life should be. It does not. The point is to test whether your choices make your life stronger or more fragile. Does this spending create joy and still respect stability? Does this goal fit your current numbers? What would need to change for it to be responsible?\n\nRich life works when values and numbers tell the truth together. If values speak without numbers, you get fantasy. If numbers speak without values, you get guilt. The better life needs both.',
			flashcards: [
				{
					id: 'school-rich-life-ramit-05-fc-1',
					role: 'main_idea',
					front: 'Where can Rich life fail?',
					back: 'When values become excuses for lifestyle creep or fragile spending.',
				},
				{
					id: 'school-rich-life-ramit-05-fc-2',
					role: 'watch_out',
					front: 'What is luxury worship?',
					back: 'Treating expensive choices as automatically meaningful.',
				},
				{
					id: 'school-rich-life-ramit-05-fc-3',
					role: 'use_it',
					front: 'How do you check it?',
					back: 'Define the value, test the tradeoff, and keep numbers honest.',
				},
			],
			applyAction: {
				type: 'buildFreedomPlan',
				label: 'Build a values based plan',
			},
			revision: 1,
		},
	],
};
